/**
 * Canonical Blueprint section configuration.
 *
 * The section *order*, display labels, and per-section accent colors are driven
 * by `docs/person_schema.json` (the hard-locked section schema) and mirror the
 * visual concept in `docs/concept.html`. The `metadata` block is rendered in
 * the MetadataBar, not as a card, so it is intentionally excluded here.
 *
 * Cards are schema-driven: `orderedSections()` interleaves this canonical order
 * with whatever section keys actually exist in a patient's document, so a card
 * is rendered for every present clinical section while preserving schema order.
 */

/** A Tailwind accent palette for one section card, per concept.html. */
export interface SectionAccent {
  /** Text color for the leading section number (e.g. "text-blue-500"). */
  number: string;
  /** Background applied to the open summary header (e.g. "bg-blue-50"). */
  openBg: string;
}

/** Static configuration for one canonical Blueprint section. */
export interface SectionConfig {
  /** The schema section key (matches the document content key). */
  key: string;
  /** Human-readable card title. */
  label: string;
  /** Per-section accent colors. */
  accent: SectionAccent;
}

/**
 * Canonical, ordered Blueprint sections (excluding `metadata`).
 *
 * Order and accents follow `person_schema.json` / `concept.html`:
 * demographics (blue) → longitudinal_metrics (indigo) → clinical_profile
 * (purple) → treatment_engine (teal) → developmental_roadmap_projections
 * (amber) → omnichannel_agent_prompts (rose).
 */
export const SECTION_CONFIGS: readonly SectionConfig[] = [
  {
    key: "demographics",
    label: "Demographics & Strengths",
    accent: { number: "text-blue-500", openBg: "bg-blue-50" },
  },
  {
    key: "longitudinal_metrics",
    label: "Longitudinal Metrics",
    accent: { number: "text-indigo-500", openBg: "bg-indigo-50" },
  },
  {
    key: "clinical_profile",
    label: "Clinical Profile & Graph",
    accent: { number: "text-purple-500", openBg: "bg-purple-50" },
  },
  {
    key: "treatment_engine",
    label: "Treatment Engine",
    accent: { number: "text-teal-500", openBg: "bg-teal-50" },
  },
  {
    key: "developmental_roadmap_projections",
    label: "Roadmap Projections",
    accent: { number: "text-amber-500", openBg: "bg-amber-50" },
  },
  {
    key: "omnichannel_agent_prompts",
    label: "Omni-Channel Agents",
    accent: { number: "text-rose-500", openBg: "bg-rose-50" },
  },
];

/** Accent used for any non-canonical section key found in a document. */
const FALLBACK_ACCENT: SectionAccent = {
  number: "text-slate-500",
  openBg: "bg-slate-50",
};

/** Section keys never rendered as a card (surfaced in the MetadataBar instead). */
const NON_CARD_KEYS = new Set(["metadata"]);

/** A resolved section ready to render as a card. */
export interface ResolvedSection extends SectionConfig {
  /** The section's markdown body (empty string when absent). */
  markdown: string;
}

/**
 * Derive a humanized label from a raw schema key (e.g. "extra_notes" →
 * "Extra Notes").
 *
 * @param key - The raw section key.
 * @returns A title-cased, space-separated label.
 */
function humanize(key: string): string {
  return key
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

/**
 * Resolve the ordered list of section cards for a document's content.
 *
 * Canonical sections come first in schema order (rendered even when empty so
 * the Blueprint structure is always visible); any extra keys present in the
 * content but not in the schema are appended with a fallback accent. The
 * `metadata` block is excluded.
 *
 * @param content - The document's section-keyed markdown (may be undefined).
 * @returns The ordered sections to render as cards.
 */
export function orderedSections(
  content: Record<string, string> | undefined,
): ResolvedSection[] {
  const body = content ?? {};
  const canonical: ResolvedSection[] = SECTION_CONFIGS.map((config) => ({
    ...config,
    markdown: body[config.key] ?? "",
  }));

  const known = new Set(SECTION_CONFIGS.map((c) => c.key));
  const extras: ResolvedSection[] = Object.keys(body)
    .filter((key) => !known.has(key) && !NON_CARD_KEYS.has(key))
    .map((key) => ({
      key,
      label: humanize(key),
      accent: FALLBACK_ACCENT,
      markdown: body[key],
    }));

  return [...canonical, ...extras];
}
